import React from "react";
import { Box, CircularProgress, DialogActions, FormControlLabel, FormHelperText, IconButton, Radio, RadioGroup } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import FeatherIcon from "feather-icons-react";
import { Formik } from "formik";
import { useSnackbar } from "../../components/toast/index.jsx";
import { DraggableDialog } from "../../consts.js";
import { USER_CREATED_FAILED, USER_CREATED_SUCCESS } from "../../helpers/messages.js";
import { addUserToGroupAPI } from "../../services/group.js";
import { createUserAPI, updateUserAttributesAPI } from "../../services/user.js";
import Utils from "../../utils.js";
import { CreateUserSchema } from "./validator.js";
import AppSelect from "../../components/common-form/select-input/index.jsx";

function CreateUserComponent({ open, onClose, companyList }) {
    const showSnackbar = useSnackbar();

    const initialValues = {
        company: "",
        email: "",
        admin: false,
    }

    /**
     * Submit Method to create a user
     */
    const submitForm = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            Utils.sendProgressBar(true);
            const response = await createUserAPI({
                Username: values.email,
            });
            await updateUserAttributesAPI({
                Username: values.email,
                UserAttributes: [
                    {
                        Name: "email_verified",
                        Value: "true",
                    },
                ],
            });
            await Utils.genericMutation("createUser", {
                id: response.User.Username,
            });
            await addUserToGroupAPI({
                GroupName: values.company,
                Username: response.User.Username
            });
            if (values.admin) {
                await addUserToGroupAPI({
                    GroupName: "admin",
                    Username: response.User.Username
                });
            }
            showSnackbar(USER_CREATED_SUCCESS)
            setSubmitting(false);
            onClose(true);
        } catch (error) {
            console.log("error create user = ", error)
            setSubmitting(false);
            showSnackbar(USER_CREATED_FAILED, "error")
        }
    }

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="xs"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                Create User
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>
            <Formik
                enableReinitialize
                validationSchema={CreateUserSchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers sx={{ paddingY: 3 }}>
                            <TextField
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth={true}
                                label="Email"
                                required
                                name={"email"}
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={touched.email ? errors.email : ''}
                                disabled={isSubmitting}
                            />
                            <AppSelect
                                name={"company"}
                                label="Company"
                                labelKey="name"
                                valueKey="internalname"
                                options={companyList}
                                value={values.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.company}
                                error={errors.company}
                                disabled={isSubmitting}
                                validationSchema={CreateUserSchema}
                                sx={{ marginTop: 3 }}
                            />
                            <FormControl sx={{ marginTop: 2 }}>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    value={values.admin}
                                    defaultValue={values.admin}
                                    name={"admin"}
                                    onChange={(e) => {
                                        const value = e.target.value === "true";
                                        setFieldValue("admin", value);
                                    }}
                                    onBlur={handleBlur}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        flexWrap: "wrap",
                                        gap: 3,
                                    }}
                                >
                                    <FormControlLabel value={true} control={<Radio />} label="Admin" />
                                    <FormControlLabel value={false} control={<Radio />} label="User" />
                                </RadioGroup>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Create"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default CreateUserComponent;